
import { exportOrderDetails, orderDetails } from 'api/order/order.js';
import BackIcon from 'components/common/BackIcon.js';
import BreadCrumb from 'components/common/BreadCrumb.js';
import ExportTable from 'components/common/ExportTable.js';
import Loader from 'components/common/Loader.js';
import NoDataFound from 'components/common/NoDataFound.js';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';

export default function OrderDetails () {
  const { id } = useParams()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(false)

  const fullShopifyId = `gid://shopify/Order/${id}`;

  useEffect(() => {
    fetchOrderDetails()
  }, [])

  const fetchOrderDetails = async () => {
    try{
      setLoading(true)
      const params = new URLSearchParams()
      params.append('id', fullShopifyId)
      const res = await orderDetails(`?${params.toString()}`)
      setOrder(res.data)
    } catch(error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch order details');
    }finally{
      setLoading(false)
    }
  }

  const handleExport = async (type) => {
    try {
      const response = await exportOrderDetails(type, fullShopifyId);

      const contentType = response.headers['content-type'];

      const blob = new Blob([response.data], { type: contentType });

      const disposition = response.headers['content-disposition'];
      let filename = `order_details.${type}`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('File Downloaded Successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to export order details');
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center ongoingProjectsPg mb-4">

          <div className="d-flex align-items-center">
            <BackIcon />
            <div className="mainPageHeader">
              <BreadCrumb title="Order Details" />
            </div>
          </div>

          <div className="d-flex">
            <ExportTable type="details" handleExport={handleExport}/>
          </div>
        </div>
        {order !== null &&
        <div>

          {/* Order Info */}
          <Card className="mb-4">
            <CardBody>
              <h5 className="mb-3">Order Info</h5>
              <Row>
                <Col md={4}><strong>Order:</strong> {order.name}</Col>
                <Col md={4}><strong>Order Date:</strong> {dayjs(order.createdAt).format('YYYY-MM-DD')}</Col>
                <Col md={4}><strong>Fulfillment Status:</strong> {order?.fulfillments && order?.fulfillments[0]?.status}</Col>
                <Col md={4}><strong>Payment Status:</strong> {order?.displayFinancialStatus}</Col>
                <Col md={4}><strong>Process Date:</strong> {dayjs(order?.processedAt).format('YYYY-MM-DD')}</Col>
                <Col md={4}><strong>Closed Date:</strong> {order.closedAt ? dayjs(order?.closedAt).format('YYYY-MM-DD') : 'N/A'}</Col>
                <Col md={4}>
                  <strong>Cancelled Date:</strong>
                  {order.cancelledAt ? dayjs(order?.cancelledAt).format('YYYY-MM-DD') : 'N/A'}
                </Col>
                <Col md={4}>
                  <strong>Tags:</strong>
                  {order?.tags?.length > 0 ? order?.tags?.join(',') : 'N/A'}
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Customer Info */}
          <Card className="mb-4">
            <CardBody>
              <h5 className="mb-3">Customer Info</h5>
              <Row>
                <Col md={6}><strong>Currency:</strong> {order.currencyCode}</Col>
                <Col md={6}><strong>Name:</strong> {order.customer.firstName + ' ' + order.customer.lastName}</Col>
                <Col md={6}><strong>Email:</strong> {order.customer.email}</Col>
                <Col md={6}><strong>Phone:</strong> {order?.customer?.phone || 'No Phone Number'}</Col>
              </Row>
            </CardBody>
          </Card>

          {/* Items Table */}
          <Card className="mb-4">
            <CardBody>
              <h5 className="mb-3">Items</h5>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Variant</th>
                    <th>Quantity</th>
                    <th>SKU</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order?.lineItems?.edges?.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item?.node?.title}</td>
                      <td>{item?.node?.variantTitle || 'N/A'}</td>
                      <td>{item?.node?.quantity}</td>
                      <td>{item?.node?.sku || 'N/A'}</td>
                      <td>{item?.node?.originalUnitPriceSet?.shopMoney?.amount}</td>
                      <td>{(item?.node?.originalUnitPriceSet?.shopMoney?.amount * item?.node?.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>

          {/* Shipping Address */}
          <Card className="mb-4">
            <CardBody>
              <h5 className="mb-3">Shipping Address</h5>
              <p className="mb-0">
                <strong>{order.shippingAddress?.name}</strong><br />
                {order.shippingAddress?.address1}<br />
                {order.shippingAddress?.city}, {order?.shipping_address?.zip}<br />
                {order.shippingAddress?.country}
              </p>
            </CardBody>
          </Card>

          {/* Totals Summary */}
          <Card className="mb-4">
            <CardBody>
              <h5 className="mb-3">Summary</h5>
              <Row>
                <Col md={4}><strong>Subtotal:</strong> {order?.subtotalPriceSet?.shopMoney?.amount}</Col>
                <Col md={4}><strong>Shipping:</strong> {order?.totalShippingPriceSet?.shopMoney?.amount}</Col>
                <Col md={4}><strong>Tax:</strong> {order?.totalTaxSet?.shopMoney?.amount}</Col>
              </Row>
              <hr />
              <h5 className="text-end">Total: {order?.totalPriceSet?.shopMoney?.amount}</h5>
            </CardBody>
          </Card>
        </div>
        }
        {order === null && loading && <Loader/>}
        {order === null && !loading && (
          <NoDataFound message="No order details available" />
        )}
      </Container>
    </div>
  )
}
